import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import FaceImage from 'assets/icons/facematch.svg';
import { HttpStatusCode, isAxiosError } from 'axios';
import { INVOICING_RECEIVED_SSE_URL } from 'common/constants';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStoreSelector } from 'v3/data/store';
import { setNavigationStep } from 'v3/data/store/contractCard/actions';
import { ContractNavigationEnum } from 'v3/data/store/contractCard/enum';
import { setContractStepperState } from 'v3/data/store/contractCard/stepper/actions';
import { ContractStepperEnum } from 'v3/data/store/contractCard/stepper/enum';
import {
  setContractingState,
  setContractSignDate,
  setTimeRemaining,
  setTokenValid,
} from 'v3/data/store/contracting/actions';
import { contractingStateMap } from 'v3/data/store/contracting/constants';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { setDevice } from 'v3/data/store/device/actions';
import { deviceInitialState } from 'v3/data/store/device/state';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { clearSeller, setSeller } from 'v3/data/store/seller/actions';
import { ContractingEventsEnum } from 'v3/data/store/seller/enum';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { setCanSendToken, setEnablePreContractDownload } from 'v3/data/store/token/action';
import { clearVoucherStatus, setVoucher, setVoucherStatus } from 'v3/data/store/voucher/actions';
import { voucherInitialState } from 'v3/data/store/voucher/state';
import { usePreferenceOptionActive } from 'v3/hooks/useIsPreferenceOptionActive';
import { useSse } from 'v3/hooks/useSse';
import { AssignContract } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/AssignContract';
import { DownloadPreContract } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/DownloadoPreContract';
import { createSeller } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/factory';
import { PurchaseInformation } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/Purchase';
import { updateNavigationStepByContractingState } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/state-navigation-mapping';
import { TokenStep } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/Token';
import { getInfoSnackbar } from 'v3/pages/Contracting/Details/Cards/ContractInformation/Steps/Token/useCase/message-warning';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import { GO_BACK } from 'v3/routes/enum';
import * as api from 'v3/services/api';
import { CafAnalysisModal } from 'v3/shared/components/Modal/CafAnalysesModal';
import { ContractingNextStepsModal } from 'v3/shared/components/Modal/ContractingNextStepsModal';
import { SellerDataModal } from 'v3/shared/components/Modal/SellerDataModal';
import { VerticalStepper } from 'v3/shared/components/VerticalStepper';
import { completedStepsByContractingStatus } from 'v3/shared/components/VerticalStepper/active-steps-mapping';
import { IStep } from 'v3/shared/components/VerticalStepper/interfaces';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { PreferencesOptionsEnum } from 'v3/shared/enums/preferences-options-enum';
import { VoucherStatusEnum } from 'v3/shared/enums/voucher-status.enum';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { TitleSmall } from 'v3/shared/typography/TitleSmall';
import { theme } from 'v3/styles/global-styles';
import { CardHeaderStyled, CardStyled, Content, UpdateMessageStyle } from '../styles';

const desiredMessageStatus = 'Applied';
export const ContractInformation = ({ readOnly }) => {
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;

  const { tokenValid, contractingState } = useStoreSelector((store) => store.contracting);
  const { imei } = useStoreSelector((store) => store.device);
  const navigate = useNavigate();

  const seller = useStoreSelector((store) => store.seller);
  const { flagSelectForPreContract } = useStoreSelector((store) => store.token);
  const [dowloadPreContractAvailable, setDowloadPreContractAvailable] = useState(false);
  const { currentStep } = useStoreSelector((store) => store.navigate);
  const { device } = useStoreSelector((store) => store);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalForNextStep, setConfirmationModalForNextStep] = useState(false);
  const isFlowDatasys = usePreferenceOptionActive(PreferencesOptionsEnum.IKCLOUD_40826_DATASYS_VOUCHER);
  const { data, connect } = useSse(`${INVOICING_RECEIVED_SSE_URL}${contractingKey}`, desiredMessageStatus);
  const [hasPreContractSignatureEvent, setHasPreContractSignatureEvent] = useState(false);
  const [sellerFeatureEnabled, setSellerFeatureEnabled] = useState(false);
  const [signContractError, setSignContractError] = useState(false);

  const shouldAllowTokenSending =
    contractingState === ContractingStateEnum.WaitingTermsAcceptance &&
    !readOnly &&
    currentStep === ContractNavigationEnum.INITIAL;

  const validStates = [
    ContractingStateEnum.WaitingContractEffectiveness,
    ContractingStateEnum.ProcessingAntiFraud,
    ContractingStateEnum.ContractingFinished,
  ];

  const isTokenValidated = validStates.includes(contractingState) && tokenValid;

  const getPreContractDownloadFlag = async () => {
    const flagResponse = await api.getFlagStatus(
      FeatureFlagEnum.IKLOUD_6002_ENABLE_PRE_CONTRACT_AFTER_TOKEN_VALIDATION,
    );
    setEnablePreContractDownload(flagResponse.data.enabled);
    setDowloadPreContractAvailable(flagResponse.data.enabled);

    updateNavigationStepByContractingState(
      contractingState,
      Boolean(device.imei),
      flagResponse.data.enabled,
      flagResponse.data.enabled,
    );
  };

  const getDeviceData = async () => {
    try {
      const response = await api.getDevice(contractingKey);
      setDevice(response.data);
      setContractStepperState({
        stepName: ContractStepperEnum.DeviceDetails,
        error: false,
        isCompleted: true,
      });
    } catch {
      setDevice(deviceInitialState);
    }
  };

  const updateNavigation = async () => {
    if (isTokenValidated) {
      await getDeviceData();
    }
  };

  useEffect(() => {
    updateNavigation();
  }, [contractingState, contractingKey]);

  useEffect(() => {
    setCanSendToken(shouldAllowTokenSending);
  }, [shouldAllowTokenSending]);

  const handleGetDevice = useCallback(async () => {
    if (contractingKey) {
      try {
        const response = await api.getDevice(contractingKey);
        setDevice(response.data);
      } catch {
        setDevice(deviceInitialState);
      }
    }
  }, [contractingKey, device]);

  useEffect(() => {
    getPreContractDownloadFlag();
    handleGetDevice();
  }, [contractingKey]);

  const isCompletedStep = (stepper: ContractStepperEnum) =>
    completedStepsByContractingStatus(
      contractingState,
      Boolean(imei),
      flagSelectForPreContract,
      dowloadPreContractAvailable,
    ).includes(stepper);

  const hasTokenValided = (): boolean => {
    const currentStatusIndex = contractingStateMap.indexOf(contractingState);
    const tokenValidedIndex = contractingStateMap.indexOf(ContractingStateEnum.WaitingTermsAcceptance);

    return currentStatusIndex > tokenValidedIndex;
  };

  const isOpenDeviceDetails = (hasTokenValided() && !dowloadPreContractAvailable) || Boolean(device.imei);

  const isOpenPreContract =
    contractingState !== ContractingStateEnum.ContractingFinished &&
    hasTokenValided() &&
    flagSelectForPreContract === true;

  const registerEventOfContractSignature = async () => {
    const createEvent = {
      sellerId: seller.seller.id,
      contractingKey,
      event: ContractingEventsEnum.ContractSignature,
    };
    await api.createContractingEvents(createEvent);
  };

  const handleContractSignature = async () => {
    try {
      setSignContractError(false);
      const response = await api.signContract(contractingKey);
      if (sellerFeatureEnabled) registerEventOfContractSignature();
      setContractingState(response.data.status);
      setContractSignDate(response.data.contractSignDate);
      showSnackbar('Contrato assinado com sucesso!', 'success', true);
      setContractStepperState({ stepName: ContractStepperEnum.ContractSignature, error: false, isCompleted: true });
      setNavigationStep(ContractNavigationEnum.CONTRACT_COMPLETED);
      setConfirmationModalForNextStep(false);
    } catch (error) {
      isFlowDatasys
        ? setSignContractError(true)
        : showErrorDialog('Erro ao assinar contrato, por favor tente novamente', error);

      clearVoucherStatus();
    } finally {
      setConfirmationModalOpen(false);
    }
  };

  const fetchSellerFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKB2C_4429_ENABLE_SELLER_FEATURE);
    if (response.data.enabled) {
      setSellerFeatureEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchSellerFeatureFlag();
  }, []);

  const steps: IStep[] = [
    {
      step: (
        <TokenStep
          onValidate={() => {
            isFlowDatasys && setConfirmationModalOpen(true);
          }}
        />
      ),
      label: 'Enviar o token para o cliente',
      stepState: [
        ContractNavigationEnum.INITIAL,
        ContractNavigationEnum.TOKEN_REQUESTED,
        ContractNavigationEnum.TOKEN_VERIFIED,
      ],
      isCompleted: isCompletedStep(ContractStepperEnum.TokenStep),
    },
    ...(flagSelectForPreContract
      ? [
          {
            step: <DownloadPreContract setDowloadPreContractAvailable={setDowloadPreContractAvailable} />,
            label: 'Baixar pré-contrato',
            stepState: ContractNavigationEnum.PRE_CONTRACT,
            isCompleted: isCompletedStep(ContractStepperEnum.PreContract),
            stayOpenAfterCompletion: isOpenPreContract,
          },
        ]
      : []),
    {
      step: <PurchaseInformation isFlowDatasys={isFlowDatasys} />,
      label: 'Informações da compra',
      stepState: ContractNavigationEnum.DEVICE_DETAILS,
      isCompleted: isCompletedStep(ContractStepperEnum.DeviceDetails),
      stayOpenAfterCompletion: isOpenDeviceDetails,
    },
    {
      step: (
        <AssignContract handleContractSignature={handleContractSignature} sellerFeatureEnabled={sellerFeatureEnabled} />
      ),
      label: 'Assinar contrato',
      subLabel: 'Conclua os passos anteriores para assinar o contrato',
      stepState: ContractNavigationEnum.SIGN_CONTRACT,
      isCompleted: isCompletedStep(ContractStepperEnum.ContractSignature),
    },
  ];
  const handleSetSeller = async () => {
    clearSeller();
    try {
      if (contractingKey) {
        const response = await api.getContractingEvents({
          contractingKey: contractingKey,
        });
        const events = response.data;
        const contractingSignatureEvent = events.find(
          (contractingEvent) => contractingEvent.event === ContractingEventsEnum.ContractSignature,
        );

        const preDataSysContractSignature = events.find(
          (contractingEvent) => contractingEvent.event === ContractingEventsEnum.preDataSysContractSignature,
        );

        if (preDataSysContractSignature) {
          setHasPreContractSignatureEvent(true);
          const seller = createSeller({
            ...preDataSysContractSignature.seller,
            id: preDataSysContractSignature.sellerId,
          });

          setSeller(seller);
        }

        if (contractingSignatureEvent) {
          const seller = createSeller({
            ...contractingSignatureEvent.seller,
            id: contractingSignatureEvent.sellerId,
          });
          setSeller(seller);
        }
      }
    } catch (error) {
      console.error('Error getting contracting events', error);
      if (isAxiosError(error) && error.response?.status === 403) {
        showSnackbar('Erro ao buscar eventos da contratação', 'error', true);
      }
    }
  };

  const registerEventOfPreDatasysContractSignature = async () => {
    const createEvent = {
      sellerId: seller.seller.id,
      contractingKey,
      event: ContractingEventsEnum.preDataSysContractSignature,
    };
    await api.createContractingEvents(createEvent);

    setHasPreContractSignatureEvent(true);
    OpenNextStepsHandler();
  };

  const OpenNextStepsHandler = () => {
    if (hasPreContractSignatureEvent) {
      connect();
      setConfirmationModalOpen(false);
      setConfirmationModalForNextStep(true);
    }
  };

  const handleVoucher = async () => {
    if (contractingKey) {
      try {
        const response = await api.getVoucher(contractingKey);
        const contractingData = await handleUpdateStatus();

        const isApplied = response.data.status === VoucherStatusEnum.Applied;

        const contractAlreadyAssigned =
          isApplied && contractingData?.status === ContractingStateEnum.ContractingFinished;

        contractAlreadyAssigned && setConfirmationModalForNextStep(false);

        const enableToSignContract =
          isApplied && contractingData?.status === ContractingStateEnum.WaitingContractEffectiveness;

        enableToSignContract && handleContractSignature();

        setVoucher(response.data);
      } catch (err) {
        setVoucher(voucherInitialState);
        console.error('Error getting voucher', err);
      }
    }
  };

  const handleAlreadyInvoiced = async () => {
    await handleVoucher();
  };

  const resetContractingFields = () => {
    setTokenValid(false);
    setTimeRemaining('');
    setContractSignDate('');
  };

  const handleUpdateStatus = async () => {
    try {
      const response = await api.getContractingState(contractingKey);
      setContractingState(response.data.status);
      setTokenValid(response.data.tokenValid);
      setTimeRemaining(response.data.timeRemaining);
      setContractSignDate(response.data.contractSignDate);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        resetContractingFields();

        if (error.response?.status === HttpStatusCode.Locked) {
          setContractingState(ContractingStateEnum.CreditAnalysisRejected);
        }
        if (error.response?.status === HttpStatusCode.Gone) {
          setContractingState(ContractingStateEnum.ExpiredKey);
        }
      } else {
        showErrorDialog('Erro ao obter status da contratação', error);
        console.error(error);
      }
    }
  };

  useEffect(() => {
    const isCafError =
      contractingState === ContractingStateEnum.CafRejected ||
      contractingState === ContractingStateEnum.ProcessingAntiFraud;
    isCafError && setConfirmationModalForNextStep(false);
  }, [contractingState]);

  useEffect(() => {
    handleSetSeller();
  }, [contractingKey]);

  useEffect(() => {
    getPreContractDownloadFlag();
  }, [contractingState]);

  useEffect(() => {
    const isInValidSignatureState =
      contractingState === ContractingStateEnum.WaitingContractEffectiveness && isFlowDatasys;
    if (isInValidSignatureState && hasPreContractSignatureEvent) {
      OpenNextStepsHandler();
    }

    if (contractingState !== ContractingStateEnum.WaitingContractEffectiveness) {
      setConfirmationModalOpen(false);
    }

    if (isInValidSignatureState && !hasPreContractSignatureEvent) {
      setConfirmationModalOpen(true);
    }
  }, [contractingState, isFlowDatasys, hasPreContractSignatureEvent]);

  useEffect(() => {
    if (data?.status === VoucherStatusEnum.Applied) {
      setVoucherStatus(data?.status);
      handleUpdateStatus();
      setConfirmationModalForNextStep(false);
    }
  }, [data]);

  return (
    <CardStyled>
      <CardHeaderStyled
        title={
          <Content>
            <HistoryEduOutlinedIcon style={{ color: theme.palette.error.main }} />
            <SubtitleLarge>Contrato</SubtitleLarge>
          </Content>
        }
      />

      {!readOnly && getInfoSnackbar(contractingState, currentStep)}
      <VerticalStepper steps={steps} currentStep={currentStep} />
      <UpdateMessageStyle>
        <TitleSmall>*Atualize o status ou atualize a página para verificar se o status alterou</TitleSmall>
      </UpdateMessageStyle>

      <SellerDataModal
        modal={{
          active: confirmationModalOpen,
          title: 'Consultor, informe seu CPF para prosseguir no processo de assinatura de contrato ',
          subtitle: 'Ao prosseguir, você precisa faturar a compra no sistema Datasys para que o contrato seja assinado',
          image: FaceImage,
          cancelButtonText: 'Cancelar',
          confirmButtonText: 'Prosseguir',
          hideCloseButton: true,
        }}
        onClose={() => {
          navigate(GO_BACK);
        }}
        onConfirm={registerEventOfPreDatasysContractSignature}
      />
      <ContractingNextStepsModal
        signContractError={signContractError}
        open={confirmationModalForNextStep}
        onClose={() => setConfirmationModalForNextStep(false)}
        onConfirm={handleAlreadyInvoiced}
      />
      <CafAnalysisModal />
    </CardStyled>
  );
};
